/**
 * QuickLoginModal - Modal for linking local player slots to accounts
 * 
 * Used in local pass-and-play to let individual players link their
 * slot to their Radio Baseball account for XP/stat tracking.
 */

import { useState } from 'react';
import { X, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { playClick, playSuccess } from '@/utils/sounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickLoginModal({ 
  playerSlot, 
  playerName, 
  onSuccess, 
  onClose,
  onSkip 
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    playClick();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      
      const { access_token, user } = response.data;
      
      playSuccess();
      toast.success(`Linked to ${user.username}'s account!`);
      
      onSuccess({
        token: access_token,
        user: user,
        playerSlot: playerSlot,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-cream rounded-lg border-4 border-ink p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-login-modal"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <User className="text-grass" size={20} />
            <h2 className="retro-heading text-lg text-ink">LINK ACCOUNT</h2>
          </div>
          <button
            onClick={() => { playClick(); onClose(); }}
            className="p-1 hover:bg-ink/10 rounded"
            data-testid="close-modal-btn"
          >
            <X size={20} className="text-ink" />
          </button>
        </div>

        {/* Slot Info */}
        <div className="bg-grass/10 p-3 rounded-lg border-2 border-grass mb-4">
          <p className="retro-text text-sm text-ink">
            Link <strong>{playerName || `Player ${playerSlot + 1}`}</strong>'s slot to a Radio Baseball account to earn XP and track stats!
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="retro-text text-xs text-ink block mb-1">EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-white"
              required
              autoFocus
              data-testid="quick-login-email"
            />
          </div>
          
          <div>
            <label className="retro-text text-xs text-ink block mb-1">PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-white"
              required
              data-testid="quick-login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="pixel-btn pixel-btn-primary w-full"
            data-testid="quick-login-submit"
          >
            {loading ? 'LINKING...' : 'LINK ACCOUNT'}
          </button>
        </form>

        {/* Skip Option */}
        <div className="mt-4 text-center">
          <button
            onClick={() => { playClick(); onSkip(); }}
            className="retro-text text-sm text-dirt hover:text-ink underline"
            data-testid="skip-link-btn"
          >
            Play as guest (no XP)
          </button>
        </div>
      </div>
    </div>
  );
}
